/**
 * styledSwing — the authored swing styles of the warrior and paladin kits
 * (plan 29 F9, lane BX 2026-10-11): rend, guillotine, cleave, ram, hammer,
 * crusade, shield_bash; the rogue's stab, flurry and lunge (lane BY); and the
 * shaman's storm (Stormstrike, lane CA). The
 * smear and Lightning Touch stay in MeleeSwingRenderer.
 *
 * Every style is anchored on the attacker's body centre and sized from the
 * swing's drawn radius (`smearDrawRadius` of the real reach, lane BB), so the
 * shapes stay inside the real hit cone; the faint real-area wash (plan 29
 * D55) is drawn under it exactly as for the smear. Pixi 8 rule: each shape
 * is drawn once at spawn and animated by setPosition/setRotation/setScale/
 * setAlpha only. Reduced motion keeps every shape at its final place and
 * only fades it.
 */
import { createGraphics } from '../../rendering/utils/Graphics';
import type { IGraphics } from '../../rendering/IRenderer';
import { MELEE_SWING_CONFIG, SWING_STYLE_LOOKS, type SwingStyleLook } from '../../../config/abilities/meleeSwing.config';
import type { MeleeSwingStyle } from '../../../shared/config/meleeSwingStyles';
import { crescentPoints, wedgePoints, crescentArcAngles, smearDrawRadius, easeOutCubic, lighten, type Point } from './meleeSwingShape';
import type { MeleeSwingDraw } from './MeleeSwingRenderer';
import { coneOutlinePoints } from '../../../shared/logic/MeleeCone';

const DEG = Math.PI / 180;

type Frame = (t: number) => void;

/** True for a style this module draws. */
export function isStyledSwing(style: MeleeSwingStyle | undefined): style is keyof typeof SWING_STYLE_LOOKS {
  return !!style && Object.prototype.hasOwnProperty.call(SWING_STYLE_LOOKS, style);
}

function animate(durationMs: number, frame: Frame, done: () => void): void {
  const start = performance.now();
  const tick = () => {
    const t = Math.min((performance.now() - start) / Math.max(1, durationMs), 1);
    frame(t);
    if (t < 1) requestAnimationFrame(tick);
    else done();
  };
  requestAnimationFrame(tick);
}

/** A point at `r` px along the right-facing angle `deg`, mirrored for left. */
function polar(r: number, deg: number, dir: 1 | -1): Point {
  return { x: Math.cos(deg * DEG) * r * dir, y: Math.sin(deg * DEG) * r };
}

/** Fade that holds full alpha until `hold` then falls to 0. */
const fadeAfter = (t: number, hold: number) => (t < hold ? 1 : Math.max(0, 1 - (t - hold) / (1 - hold)));

/** Mirror a list of right-facing local points for the facing. */
const faced = (pts: Point[], dir: 1 | -1): Point[] => pts.map((p) => ({ x: p.x * dir, y: p.y }));

interface Ctx {
  scene: unknown;
  d: MeleeSwingDraw;
  look: SwingStyleLook;
  radius: number;
  dir: 1 | -1;
  /** Middle of the drawn swing, degrees (right-facing basis). */
  midDeg: number;
  color: number;
  accent: number;
  motion: boolean;
}

/** Accent drawers: return the graphics and the per-frame animation. */
type AccentDrawer = (c: Ctx) => { gs: IGraphics[]; frame: Frame };
type RogueAccent = 'dagger' | 'cross_cuts' | 'thrust';
type ShamanAccent = 'thunderbolt';
const ACCENTS: Record<Exclude<SwingStyleLook['accent'], RogueAccent | ShamanAccent>, AccentDrawer> = {
  // Mortal Strike: two parallel slanted cuts left at the reach — the wound.
  gash(c) {
    const at = polar(c.radius * 0.92, c.midDeg, c.dir);
    const g = createGraphics(c.scene, { depth: MELEE_SWING_CONFIG.depth + 2 });
    g.setBlendMode('add');
    const len = c.radius * 0.3;
    for (const off of [-7, 7]) {
      const a = { x: (-len / 2) * 0.5 * c.dir + off * c.dir, y: -len / 2 };
      const b = { x: (len / 2) * 0.5 * c.dir + off * c.dir, y: len / 2 };
      g.strokeLine(a.x, a.y, b.x, b.y, c.color, 6, 0.9);
      g.strokeLine(a.x, a.y, b.x, b.y, c.accent, 2, 1);
    }
    g.setPosition(c.d.x + at.x, c.d.y + at.y);
    return {
      gs: [g],
      frame: (t) => {
        // The cuts open across the target as the blade passes, then linger.
        if (c.motion) g.setScale(0.35 + 0.65 * easeOutCubic(t / 0.3), 1);
        g.setAlpha(t < 0.15 ? t / 0.15 : fadeAfter(t, 0.55));
      },
    };
  },

  // Execute: a heavy blade falls onto the reach, then a cut flash.
  drop_blade(c) {
    const at = polar(c.radius * 0.78, c.midDeg, c.dir);
    const blade = createGraphics(c.scene, { depth: MELEE_SWING_CONFIG.depth + 2 });
    const h = c.radius * 0.5;
    blade.fillPolygon([{ x: -7, y: -h }, { x: 7, y: -h }, { x: 9, y: 0 }, { x: -9, y: 4 }], c.color, 0.9);
    blade.strokeLine(-9, 4, 9, 0, c.accent, 3, 1);
    const flash = createGraphics(c.scene, { depth: MELEE_SWING_CONFIG.depth + 3 });
    flash.setBlendMode('add');
    flash.strokeLine(-c.radius * 0.28, 0, c.radius * 0.28, 0, c.accent, 4, 1);
    flash.fillCircle(0, 0, 6, c.accent, 0.9);
    const drop = c.radius * 0.35;
    flash.setPosition(c.d.x + at.x, c.d.y + at.y);
    blade.setPosition(c.d.x + at.x, c.d.y + at.y - (c.motion ? drop : 0));
    return {
      gs: [blade, flash],
      frame: (t) => {
        const fall = Math.min(1, t / 0.35);
        // Ease-in: slow at the top, fast on the cut.
        if (c.motion) blade.setPosition(c.d.x + at.x, c.d.y + at.y - drop * (1 - fall * fall));
        blade.setAlpha(fadeAfter(t, 0.6));
        flash.setAlpha(t < 0.33 ? 0 : fadeAfter((t - 0.33) / 0.67, 0.25));
        if (c.motion) flash.setScale(t < 0.33 ? 0.6 : 0.6 + 0.6 * easeOutCubic((t - 0.33) / 0.3), 1);
      },
    };
  },

  // Cleave: three steel glints along the reach arc, start to end.
  glints(c) {
    const g = createGraphics(c.scene, { depth: MELEE_SWING_CONFIG.depth + 2 });
    g.setBlendMode('add');
    const start = c.d.arcAngle;
    const end = c.d.arcAngle + c.d.arcSpread;
    for (const deg of [start + 4, (start + end) / 2, end - 4]) {
      const p = polar(c.radius, deg, c.dir);
      g.strokeLine(p.x - 7, p.y, p.x + 7, p.y, c.accent, 2, 1);
      g.strokeLine(p.x, p.y - 7, p.x, p.y + 7, c.accent, 2, 1);
      g.fillCircle(p.x, p.y, 3, c.accent, 1);
    }
    g.setPosition(c.d.x, c.d.y);
    return { gs: [g], frame: (t) => g.setAlpha(t < 0.2 ? 0 : fadeAfter((t - 0.2) / 0.8, 0.3)) };
  },

  // Charge: three chevrons driven forward and a shock front at the reach.
  chevrons(c) {
    const g = createGraphics(c.scene, { depth: MELEE_SWING_CONFIG.depth + 1 });
    g.setBlendMode('add');
    const r = c.radius;
    for (const [i, u] of [0.3, 0.48, 0.66].entries()) {
      const x = r * u;
      const s = 10 + i * 4;
      g.strokePolyline(faced([{ x: x - s, y: -s }, { x, y: 0 }, { x: x - s, y: s }], c.dir), c.color, 5, 0.55 + i * 0.15);
    }
    const front = createGraphics(c.scene, { depth: MELEE_SWING_CONFIG.depth + 2 });
    front.setBlendMode('add');
    const a = crescentArcAngles({ arcAngleDeg: -30, arcSpreadDeg: 50, facingLeft: c.dir < 0 });
    front.strokeArc(0, 0, r * 0.82, a.start, a.end, c.accent, c.look.edgeWidth, 1);
    front.strokeArc(0, 0, r * 0.82, a.start, a.end, c.color, c.look.edgeWidth + 6, 0.3);
    const slide = r * 0.2;
    g.setPosition(c.d.x - (c.motion ? slide * c.dir : 0), c.d.y);
    front.setPosition(c.d.x, c.d.y);
    return {
      gs: [g, front],
      frame: (t) => {
        if (c.motion) g.setPosition(c.d.x - slide * c.dir * (1 - easeOutCubic(t / 0.5)), c.d.y);
        g.setAlpha(fadeAfter(t, 0.35));
        front.setAlpha(t < 0.25 ? 0 : fadeAfter((t - 0.25) / 0.75, 0.2));
        if (c.motion) front.setScale(t < 0.25 ? 0.85 : 0.85 + 0.15 * easeOutCubic((t - 0.25) / 0.4));
      },
    };
  },

  // Paladin auto: a hammer head on its haft, swung around the arc.
  hammer_head(c) {
    const g = createGraphics(c.scene, { depth: MELEE_SWING_CONFIG.depth + 2 });
    const r = c.radius;
    const headW = Math.max(14, r * 0.2);
    const headH = Math.max(22, r * 0.34);
    // Drawn along angle 0 (right); the graphic rotates to the swing angle.
    g.strokeLine(r * 0.22, 0, r * 0.78, 0, 0x8a6a3a, 5, 1);
    g.fillRoundedRect(r * 0.78 - headW / 2, -headH / 2, headW, headH, 4, c.color, 1);
    g.strokeRoundedRect(r * 0.78 - headW / 2, -headH / 2, headW, headH, 4, c.accent, 2, 1);
    g.fillRect(r * 0.78 - 2, -headH / 2 + 3, 4, headH - 6, c.accent, 0.8);
    g.setPosition(c.d.x, c.d.y);
    const from = c.d.arcAngle * DEG;
    const to = (c.d.arcAngle + c.d.arcSpread) * DEG;
    const at = (u: number) => (c.dir > 0 ? from + (to - from) * u : Math.PI - (from + (to - from) * u));
    g.setRotation(at(c.motion ? 0 : 1));
    return {
      gs: [g],
      frame: (t) => {
        if (c.motion) g.setRotation(at(easeOutCubic(t / 0.55)));
        g.setAlpha(fadeAfter(t, 0.55));
      },
    };
  },

  // Crusader Strike: a holy cross flaring at the reach, ringed.
  cross(c) {
    const at = polar(c.radius * 0.86, c.midDeg, c.dir);
    const g = createGraphics(c.scene, { depth: MELEE_SWING_CONFIG.depth + 2 });
    g.setBlendMode('add');
    const arm = c.radius * 0.2;
    g.fillRect(-3.5, -arm * 1.3, 7, arm * 2.3, c.accent, 0.95);
    g.fillRect(-arm, -arm * 0.55, arm * 2, 7, c.accent, 0.95);
    g.strokeCircle(0, 0, arm * 1.15, c.color, 3, 0.8);
    g.fillCircle(0, 0, 5, 0xffffff, 1);
    g.setPosition(c.d.x + at.x, c.d.y + at.y);
    return {
      gs: [g],
      frame: (t) => {
        if (c.motion) g.setScale(0.5 + 0.65 * easeOutCubic(t / 0.4));
        g.setAlpha(t < 0.12 ? t / 0.12 : fadeAfter(t, 0.5));
      },
    };
  },

  // Shield of the Righteous: the shield thrusts to the reach; a shock front.
  shield(c) {
    const g = createGraphics(c.scene, { depth: MELEE_SWING_CONFIG.depth + 2 });
    const w = Math.max(16, c.radius * 0.18);
    const h = Math.max(26, c.radius * 0.32);
    const kite: Point[] = [{ x: 0, y: -h / 2 }, { x: w / 2, y: -h / 2 + 4 }, { x: w / 2, y: h * 0.1 }, { x: 0, y: h / 2 }, { x: -w / 2, y: h * 0.1 }, { x: -w / 2, y: -h / 2 + 4 }];
    g.fillPolygon(kite, c.color, 0.95);
    g.strokePolyline([...kite, kite[0]], c.accent, 3, 1);
    g.fillRect(-2, -h / 2 + 6, 4, h * 0.75, c.accent, 0.85);
    g.fillRect(-w / 2 + 3, -h * 0.12, w - 6, 4, c.accent, 0.85);
    const startX = MELEE_SWING_CONFIG.wedgeInnerPx * c.dir;
    const endX = c.radius * 0.62 * c.dir;
    g.setPosition(c.d.x + (c.motion ? startX : endX), c.d.y - 4);
    const front = createGraphics(c.scene, { depth: MELEE_SWING_CONFIG.depth + 1 });
    front.setBlendMode('add');
    const a = crescentArcAngles({ arcAngleDeg: -28, arcSpreadDeg: 46, facingLeft: c.dir < 0 });
    front.strokeArc(0, 0, c.radius * 0.84, a.start, a.end, c.accent, c.look.edgeWidth, 1);
    front.strokeArc(0, 0, c.radius * 0.84, a.start, a.end, c.color, c.look.edgeWidth + 6, 0.35);
    front.setPosition(c.d.x, c.d.y);
    return {
      gs: [g, front],
      frame: (t) => {
        if (c.motion) g.setPosition(c.d.x + startX + (endX - startX) * easeOutCubic(t / 0.4), c.d.y - 4);
        g.setAlpha(fadeAfter(t, 0.6));
        front.setAlpha(t < 0.3 ? 0 : fadeAfter((t - 0.3) / 0.7, 0.2));
        if (c.motion) front.setScale(t < 0.3 ? 0.85 : 0.85 + 0.2 * easeOutCubic((t - 0.3) / 0.4));
      },
    };
  },
};

/**
 * The rogue accents (lane BY) — kept beside the warrior/paladin set: the
 * same Ctx, drawn once, transform/alpha only, reduced motion at the final
 * place.
 */
const ROGUE_ACCENTS: Record<RogueAccent, AccentDrawer> = {
  // Backstab: a short blade darts from the hand to the reach, then a puncture star.
  dagger(c) {
    const at = polar(c.radius * 0.82, c.midDeg, c.dir);
    const blade = createGraphics(c.scene, { depth: MELEE_SWING_CONFIG.depth + 2 });
    const len = Math.max(18, c.radius * 0.3);
    blade.fillPolygon(faced([{ x: -len, y: -2 }, { x: 0, y: -4 }, { x: len * 0.35, y: 0 }, { x: 0, y: 4 }, { x: -len, y: 2 }], c.dir), c.accent, 0.95);
    blade.strokeLine(-len * 1.35 * c.dir, 0, -len * c.dir, 0, c.color, 4, 0.9);
    const puncture = createGraphics(c.scene, { depth: MELEE_SWING_CONFIG.depth + 3 });
    puncture.setBlendMode('add');
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      const r = i % 2 === 0 ? 16 : 9;
      puncture.strokeLine(0, 0, Math.cos(a) * r, Math.sin(a) * r, c.accent, 2, 1);
    }
    puncture.fillCircle(0, 0, 4, 0xffffff, 1);
    puncture.strokeCircle(0, 0, 11, c.color, 3, 0.8);
    const from = polar(MELEE_SWING_CONFIG.wedgeInnerPx, c.midDeg, c.dir);
    blade.setPosition(c.d.x + (c.motion ? from.x : at.x), c.d.y + (c.motion ? from.y : at.y));
    puncture.setPosition(c.d.x + at.x, c.d.y + at.y);
    return {
      gs: [blade, puncture],
      frame: (t) => {
        const u = easeOutCubic(t / 0.3);
        if (c.motion) blade.setPosition(c.d.x + from.x + (at.x - from.x) * u, c.d.y + from.y + (at.y - from.y) * u);
        blade.setAlpha(fadeAfter(t, 0.45));
        puncture.setAlpha(t < 0.28 ? 0 : fadeAfter((t - 0.28) / 0.72, 0.3));
        if (c.motion) puncture.setScale(t < 0.28 ? 0.5 : 0.5 + 0.6 * easeOutCubic((t - 0.28) / 0.35));
      },
    };
  },

  // Eviscerate: two cuts cross over the reach point, one after the other.
  cross_cuts(c) {
    const at = polar(c.radius * 0.84, c.midDeg, c.dir);
    const len = c.radius * 0.42;
    const cut = (angle: number) => {
      const g = createGraphics(c.scene, { depth: MELEE_SWING_CONFIG.depth + 2 });
      g.setBlendMode('add');
      const dx = Math.cos(angle) * len / 2;
      const dy = Math.sin(angle) * len / 2;
      g.strokeLine(-dx, -dy, dx, dy, c.color, 7, 0.85);
      g.strokeLine(-dx, -dy, dx, dy, c.accent, 2, 1);
      g.setPosition(c.d.x + at.x, c.d.y + at.y);
      return g;
    };
    const a = cut(-Math.PI / 4);
    const b = cut(Math.PI / 4);
    const drops = createGraphics(c.scene, { depth: MELEE_SWING_CONFIG.depth + 1 });
    for (const [x, y] of [[6, 10], [-8, 14], [12, 18], [-2, 22]]) drops.fillCircle(x * c.dir, y, 2.5, c.color, 0.9);
    drops.setPosition(c.d.x + at.x, c.d.y + at.y);
    return {
      gs: [a, b, drops],
      frame: (t) => {
        if (c.motion) {
          a.setScale(0.2 + 0.8 * easeOutCubic(t / 0.22), 1);
          b.setScale(t < 0.22 ? 0.2 : 0.2 + 0.8 * easeOutCubic((t - 0.22) / 0.22), 1);
          drops.setPosition(c.d.x + at.x, c.d.y + at.y + 10 * easeOutCubic(t));
        }
        a.setAlpha(t < 0.05 ? 0 : fadeAfter(t, 0.5));
        b.setAlpha(t < 0.22 ? 0 : fadeAfter(t, 0.55));
        drops.setAlpha(t < 0.3 ? 0 : fadeAfter((t - 0.3) / 0.7, 0.3));
      },
    };
  },

  // Assassinate: one long thrust line to the reach and a dark ring closing on its point.
  thrust(c) {
    const at = polar(c.radius * 0.95, c.midDeg, c.dir);
    const from = polar(MELEE_SWING_CONFIG.wedgeInnerPx, c.midDeg, c.dir);
    const line = createGraphics(c.scene, { depth: MELEE_SWING_CONFIG.depth + 2 });
    line.setBlendMode('add');
    line.strokeLine(0, 0, at.x - from.x, at.y - from.y, c.color, 8, 0.6);
    line.strokeLine(0, 0, at.x - from.x, at.y - from.y, c.accent, 2, 1);
    line.setPosition(c.d.x + from.x, c.d.y + from.y);
    const ring = createGraphics(c.scene, { depth: MELEE_SWING_CONFIG.depth + 3 });
    ring.strokeCircle(0, 0, 20, c.color, 4, 0.9);
    ring.strokeCircle(0, 0, 20, c.accent, 1.5, 1);
    ring.fillCircle(0, 0, 3.5, 0xffffff, 1);
    ring.setPosition(c.d.x + at.x, c.d.y + at.y);
    return {
      gs: [line, ring],
      frame: (t) => {
        if (c.motion) line.setScale(Math.max(0.05, easeOutCubic(t / 0.25)));
        line.setAlpha(fadeAfter(t, 0.4));
        // The ring closes from wide onto the point as the thrust lands.
        if (c.motion) ring.setScale(t < 0.2 ? 2.2 : 2.2 - 1.6 * easeOutCubic((t - 0.2) / 0.35));
        ring.setAlpha(t < 0.2 ? t / 0.2 * 0.6 : fadeAfter(t, 0.6));
      },
    };
  },
};

/**
 * The shaman accent (lane CA): Stormstrike's bolt. Same rules — drawn once,
 * transform/alpha only, reduced motion at the final place.
 */
const SHAMAN_ACCENTS: Record<ShamanAccent, AccentDrawer> = {
  // Stormstrike: a jagged bolt falls onto the reach point, then a white crack.
  thunderbolt(c) {
    const at = polar(c.radius * 0.86, c.midDeg, c.dir);
    const h = Math.max(36, c.radius * 0.6);
    const zig: Point[] = [{ x: -4, y: -h }, { x: 6, y: -h * 0.66 }, { x: -6, y: -h * 0.4 }, { x: 5, y: -h * 0.16 }, { x: 0, y: 0 }];
    const boltG = createGraphics(c.scene, { depth: MELEE_SWING_CONFIG.depth + 2 });
    boltG.setBlendMode('add');
    boltG.strokePolyline(faced(zig, c.dir), c.color, 8, 0.45);
    boltG.strokePolyline(faced(zig, c.dir), c.accent, 3, 1);
    boltG.strokePolyline(faced(zig, c.dir), 0xffffff, 1.2, 1);
    const crack = createGraphics(c.scene, { depth: MELEE_SWING_CONFIG.depth + 3 });
    crack.setBlendMode('add');
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2 + Math.PI / 6;
      crack.strokeLine(0, 0, Math.cos(a) * 15, Math.sin(a) * 15, c.accent, 2, 1);
    }
    crack.fillCircle(0, 0, 4.5, 0xffffff, 1);
    crack.strokeCircle(0, 0, 12, c.color, 3, 0.8);
    const drop = h * 0.5;
    boltG.setPosition(c.d.x + at.x, c.d.y + at.y - (c.motion ? drop : 0));
    crack.setPosition(c.d.x + at.x, c.d.y + at.y);
    return {
      gs: [boltG, crack],
      frame: (t) => {
        const fall = Math.min(1, t / 0.25);
        if (c.motion) boltG.setPosition(c.d.x + at.x, c.d.y + at.y - drop * (1 - fall * fall));
        boltG.setAlpha(t < 0.05 ? t / 0.05 : fadeAfter(t, 0.45));
        crack.setAlpha(t < 0.25 ? 0 : fadeAfter((t - 0.25) / 0.75, 0.25));
        if (c.motion) crack.setScale(t < 0.25 ? 0.5 : 0.5 + 0.7 * easeOutCubic((t - 0.25) / 0.35));
      },
    };
  },
};

const ACCENT_DRAWERS: Record<SwingStyleLook['accent'], AccentDrawer> = { ...ACCENTS, ...ROGUE_ACCENTS, ...SHAMAN_ACCENTS };

export function drawStyledSwing(scene: unknown, d: MeleeSwingDraw, style: keyof typeof SWING_STYLE_LOOKS): IGraphics[] {
  const cfg = MELEE_SWING_CONFIG;
  const look = SWING_STYLE_LOOKS[style];
  const facingLeft = !d.isRight;
  const dir: 1 | -1 = facingLeft ? -1 : 1;
  const radius = smearDrawRadius(d.arcRadius, cfg.visualReachScale, cfg.visualReachMinPx);
  const motion = !d.reducedMotion;
  const all: IGraphics[] = [];

  // Plan 29 D55: the real hit area, under everything (as for the smear).
  const wash = d.cone ? createGraphics(scene, { depth: cfg.depth - 1 }) : null;
  if (wash && d.cone) {
    wash.setBlendMode('add');
    // Same polygon as MeleeSwingRenderer.meleeRealAreaPoints (the hit test's outline).
    wash.fillPolygon(coneOutlinePoints({ x: 0, y: 0 }, facingLeft ? 'left' : 'right', d.cone, 24), d.color, cfg.realAreaAlpha);
    wash.setPosition(d.x, d.y);
    all.push(wash);
  }

  let blade: IGraphics | null = null;
  if (look.crescent) {
    const spec = { reach: radius, arcAngleDeg: d.arcAngle, arcSpreadDeg: d.arcSpread, thicknessFraction: look.thickness, facingLeft };
    blade = createGraphics(scene, { depth: cfg.depth });
    blade.setBlendMode('add');
    blade.fillPolygon(wedgePoints(spec, cfg.wedgeInnerPx), d.color, cfg.wedgeAlpha);
    blade.fillPolygon(crescentPoints(spec), d.color, look.smearAlpha);
    if (look.innerEcho > 0) {
      blade.fillPolygon(crescentPoints({ ...spec, reach: radius * look.innerEcho }), d.color, look.smearAlpha * 0.75);
    }
    const arc = crescentArcAngles(spec);
    blade.strokeArc(0, 0, radius, arc.start, arc.end, lighten(d.color, look.edgeLighten), look.edgeWidth, cfg.alpha);
    blade.setPosition(d.x, d.y);
    all.push(blade);
  }

  const accent = (ACCENT_DRAWERS[look.accent])({
    scene, d, look, radius, dir, motion,
    midDeg: d.arcAngle + d.arcSpread / 2,
    color: d.color,
    accent: lighten(d.color, look.accentLighten),
  });
  all.push(...accent.gs);

  const lead = -dir * look.sweepLeadDeg * DEG;
  const frame: Frame = (t) => {
    if (blade) {
      if (motion && look.sweepLeadDeg) {
        const e = easeOutCubic(t / 0.6);
        blade.setRotation(lead * (1 - e));
        blade.setScale(cfg.startScale + (1 - cfg.startScale) * e);
      }
      blade.setAlpha(fadeAfter(t, 0.4));
    }
    wash?.setAlpha(fadeAfter(t, 0.4));
    accent.frame(t);
  };
  frame(0);
  animate(Math.max(d.duration, look.durationMs), frame, () => all.forEach((g) => g.destroy()));
  return all;
}
